"use client";

import { getCalApi } from "@calcom/embed-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTrackEvent } from "@/hooks/useTrackEvent";

type LocalLessonCalStepProps = {
  calendarLink: string;
  customerName: string;
  customerEmail: string;
  productKey: string;
  locationSlug: string;
  onBookingSelected: (bookingUid: string) => void;
  onLoadError?: () => void;
};

function toEmbedLink(value: string) {
  try {
    const url = new URL(value);
    if (url.hostname !== "cal.com" && !url.hostname.endsWith(".cal.com")) return null;
    return `${url.pathname.replace(/^\/+/, "")}${url.search}`;
  } catch {
    return null;
  }
}

export function LocalLessonCalStep({
  calendarLink,
  customerName,
  customerEmail,
  productKey,
  locationSlug,
  onBookingSelected,
  onLoadError,
}: LocalLessonCalStepProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const onBookingSelectedRef = useRef(onBookingSelected);
  const onLoadErrorRef = useRef(onLoadError);
  const loadErrorReportedRef = useRef(false);
  const completedRef = useRef(false);
  const [loadError, setLoadError] = useState(false);
  const trackEvent = useTrackEvent({ includeAttribution: false });
  const embedLink = useMemo(() => toEmbedLink(calendarLink), [calendarLink]);

  useEffect(() => {
    onBookingSelectedRef.current = onBookingSelected;
  }, [onBookingSelected]);

  useEffect(() => {
    onLoadErrorRef.current = onLoadError;
  }, [onLoadError]);

  useEffect(() => {
    if (embedLink || loadErrorReportedRef.current) return;
    loadErrorReportedRef.current = true;
    onLoadErrorRef.current?.();
  }, [embedLink]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !embedLink) return;
    const calendarContainer = container;
    const calendarEmbedLink = embedLink;
    let cancelled = false;
    const calendarTitle = "Choose a live singing lesson time with Liuba Doga";
    const labelCalendarIframe = () => {
      const iframe = calendarContainer.querySelector("iframe");
      if (!iframe) return;
      iframe.title = calendarTitle;
    };
    const iframeObserver = new MutationObserver(labelCalendarIframe);
    iframeObserver.observe(calendarContainer, { childList: true, subtree: true });

    async function mountCalendar() {
      try {
        const cal = await getCalApi();
        if (cancelled) return;
        calendarContainer.innerHTML = "";
        cal("init");
        trackEvent("first_booking_started", {
          product_type: "local_lesson",
          product_key: productKey,
          location_slug: locationSlug,
          booking_provider: "calcom",
        });
        cal("on", {
          action: "bookingSuccessfulV2",
          callback: (event: { detail?: { data?: Record<string, unknown> } }) => {
            if (completedRef.current) return;
            const data = event.detail?.data ?? {};
            const uid = typeof data.uid === "string" ? data.uid.trim() : "";
            if (!uid) {
              setLoadError(true);
              if (!loadErrorReportedRef.current) {
                loadErrorReportedRef.current = true;
                onLoadErrorRef.current?.();
              }
              return;
            }
            completedRef.current = true;
            trackEvent("first_booking_confirmed", {
              product_type: "local_lesson",
              product_key: productKey,
              location_slug: locationSlug,
              booking_provider: "calcom",
            });
            onBookingSelectedRef.current(uid);
          },
        });
        cal("inline", {
          elementOrSelector: calendarContainer,
          calLink: calendarEmbedLink,
          config: {
            layout: "month_view",
            theme: "light",
            prefill: {
              name: customerName,
              email: customerEmail,
            },
          },
        });
        labelCalendarIframe();
      } catch {
        if (!cancelled) {
          setLoadError(true);
          if (!loadErrorReportedRef.current) {
            loadErrorReportedRef.current = true;
            onLoadErrorRef.current?.();
          }
        }
      }
    }

    void mountCalendar();
    return () => {
      cancelled = true;
      iframeObserver.disconnect();
      try {
        calendarContainer.innerHTML = "";
      } catch {
        // Cal owns the embedded DOM and may already have removed it.
      }
    };
  }, [customerEmail, customerName, embedLink, locationSlug, productKey, trackEvent]);

  if (loadError || !embedLink) {
    return (
      <div role="alert" className="rounded-[18px] border border-[#e8a7b4] bg-[#fff0f2] p-4 text-sm font-semibold leading-6 text-[#9f1239]">
        Liuba&apos;s calendar could not be loaded. Go back and try again, or contact the team if the problem continues.
      </div>
    );
  }

  return (
    <div data-clarity-mask="true" className="space-y-3">
      <div
        ref={containerRef}
        data-testid="local-lesson-calcom-calendar"
        role="region"
        aria-label="Choose a live singing lesson time with Liuba Doga"
        className="h-[min(42rem,68dvh)] min-h-[34rem] w-full overflow-hidden rounded-[18px] border border-[#d8cab6] bg-white"
      />
      <p className="text-xs font-semibold leading-5 text-[#61706b]">
        Times come directly from Liuba&apos;s Cal.com calendar. After you choose a time, you will continue to secure payment.
      </p>
    </div>
  );
}
